import * as React from "react"

import { Button, cn } from "@coval/ui"
import { Filter } from "lucide-react"

interface FilterMenuButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  /** How many filters are applied. Zero leaves the button in its resting state. */
  activeCount: number
  /** Accessible name, for a menu that filters something other than results. */
  label?: string
}

/**
 * The trigger for a filter menu: a funnel that fills with the selection blue
 * and grows a count once anything is filtered.
 *
 * The count sits inside the button rather than floating over its corner so the
 * button reads as one control, and every filter menu in the product shares this
 * one so an applied filter looks the same wherever it was applied.
 */
export const FilterMenuButton = React.forwardRef<
  HTMLButtonElement,
  FilterMenuButtonProps
>(({ activeCount, label = "Filter options", className, ...props }, ref) => {
  const isActive = activeCount > 0

  return (
    <Button
      ref={ref}
      type="button"
      variant="secondary"
      icon={!isActive}
      // Named the same way in both modes: `icon` is a runtime choice, and the
      // label has to hold when the button is showing its count instead.
      aria-label={label}
      tooltip={label}
      className={cn(
        // Filled with the chip's own blue, so the control that produced the
        // chips matches them. The transparent border holds the resting
        // button's size; the hover override keeps the fill from greying out.
        isActive &&
          "border-transparent bg-coval-info-surface text-coval-info-on-surface hover:bg-coval-info-surface",
        className,
      )}
      {...props}
    >
      <Filter />
      {isActive && (
        <span className="text-sm font-medium tabular-nums">{activeCount}</span>
      )}
    </Button>
  )
})

FilterMenuButton.displayName = "FilterMenuButton"
